import { BigNumber } from 'ethers'
import { useEffect, useState } from 'react'
import { getContractManager } from './index'


export function useContract() {
  const manager = getContractManager()
  const maxSupply = 8888;
  const [refreshCount, setRefreshCount] = useState(0)
  const [address, setAddress] = useState(null)
  const [saleActive, setSaleActive] = useState(false)
  const [wethAllowance, setWethAllowance] = useState(BigNumber.from(0))
  const [wethBalance, setWethBalance] = useState(BigNumber.from(0))
  const [maxMintCount, setMaxMintCount] = useState(0)
  const [totalSupply, setTotalSupply] = useState(0)
  const [contractError, setContractError] = useState(null)
  const [confirmingTransaction, setConfirmingTransaction] = useState(false)

  const [lastTransactionHash, setLastTransactionHash] = useState(null)

  async function buyImage(numberOfTokens) {
    return wrapTx(() => manager.buyImageToken(numberOfTokens))
  }

  async function buyVideo(numberOfTokens) {
    return wrapTx(() => manager.buyVideoToken(numberOfTokens))
  }

  async function buyCoin(numberOfTokens) {
    return wrapTx(() => manager.buyCoinToken(numberOfTokens))
  }

  async function approveWETH() {
    return wrapTx(() => manager.approveWETH())
  }

  /** @param{() => Promise<import('@ethersproject/abstract-provider').TransactionResponse>} runFn */
  async function wrapTx(runFn) {
    const state = {
      errorMessage: null,
      transactionHash: null
    }

    try {
      setConfirmingTransaction(true)
      setContractError(null)

      const tx = await runFn()

      state.transactionHash = tx.hash
      setLastTransactionHash(tx.hash)

      await tx.wait(2)
    } catch (e) {
      console.error(e)
      state.errorMessage = e.message
      setContractError(e.message)
    } finally {
      setConfirmingTransaction(false)
      setRefreshCount(refreshCount + 1)
    }

    return state
  }

  useEffect(() => {
    manager.onAccountsChanged(setAddress)

    return () => {
      manager.offAccountsChanged(setAddress)
    }
  }, [manager])

  useEffect(() => {
    const storefront = manager.readonlyStorefront
    /** @type {import('./types/BeyondStreetCred').BeyondStreetCred} */
    const token = manager.token
    const weth = manager.weth

    storefront.saleHasStarted().then((val) => {
      setSaleActive(val)
    })

    storefront.maxPurchaseCount().then((val) => {
      setMaxMintCount(val)
    })

    token.remainingMintableImages().then((val) => {
      setTotalSupply(maxSupply - val.toNumber())
    })

    // token..then((val) => {
    //   setTotalSupply(val.toNumber())
    // })

    if (address) {
      weth.balanceOf(address).then((val) => {
        setWethBalance(val)
      })

      weth.allowance(address, storefront.address).then((val) => {
        setWethAllowance(val)
      })
    } else {
      setWethBalance(BigNumber.from(0))
      setWethAllowance(BigNumber.from(0))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, refreshCount])

  return {
    address,
    isConnected: !!address,
    saleActive,
    maxSupply,
    totalSupply,
    wethAllowance,
    wethBalance,
    remainingSupply: maxSupply - totalSupply,
    maxMintCount,
    confirmingTransaction,
    lastTransactionHash,
    contractError,
    connectWallet: () => manager.connectWallet(),
    buyImage,
    buyVideo,
    buyCoin,
    approveWETH
  }
}

/**
 * @param {number} numberOfTokens
 * @param {'imagePrice' | 'videoPrice' | 'coinPrice'} pricingMethod
 */
function usePurchasePrice(numberOfTokens, pricingMethod) {
  const [loading, setLoading] = useState(false)
  const [price, setPrice] = useState(BigNumber.from(0))
  const [maticPrice, setMaticPrice] = useState(BigNumber.from(0))

  const storefront = getContractManager().readonlyStorefront
  const maticStorefront = getContractManager().maticStorefront

  useEffect(() => {
    setLoading(true)

    Promise.all([
      storefront[pricingMethod](numberOfTokens),
      maticStorefront[pricingMethod](numberOfTokens),
    ]).then(([val, maticVal]) => {
      setPrice(val)
      setMaticPrice(maticVal)
    }).finally(() => setLoading(false))
  }, [numberOfTokens, pricingMethod, storefront, maticStorefront])

  return {
    loading,
    price,
    maticPrice
  }
}

export function useImagePrice(numberOfTokens) {
  return usePurchasePrice(numberOfTokens, 'imagePrice')
}

export function useVideoPrice(numberOfTokens) {
  return usePurchasePrice(numberOfTokens, 'videoPrice')
}

export function useCoinPrice(numberOfTokens) {
  return usePurchasePrice(numberOfTokens, 'coinPrice')
}
